import type { Expediente } from '../expedientes/cliente-expedientes.js'
import { ErrorApi } from '../identidad/cliente-identidad.js'
import { cerrarCarga, mostrarCarga, notificar } from '../fundacion/alertas.js'
import {
  agregarEvidencia,
  completarObservacion,
  crearObservacion,
  listarEvidencias,
  listarObservaciones,
  type Observacion,
  type TipoObservacion,
  type CategoriaObservacion,
} from './cliente-observaciones.js'

const elemento = <T extends Element>(selector: string): T => {
  const dato = document.querySelector<T>(selector)
  if (!dato) throw new Error(`No existe ${selector}`)
  return dato
}
const textoError = (error: unknown) =>
  error instanceof ErrorApi ? error.message : 'No fue posible completar la operación.'
let expediente: Expediente | null = null
let roles: string[] = []

function informar(mensaje: string, tipo = 'success') {
  const alerta = elemento<HTMLElement>('#alerta-observaciones')
  alerta.className = 'alert d-none'
  notificar(mensaje, tipo === 'danger' ? 'error' : (tipo as 'success' | 'warning' | 'info'))
}

async function tarjeta(dato: Observacion): Promise<HTMLElement> {
  const articulo = document.createElement('article')
  articulo.className = 'card border mb-3'
  const cuerpo = document.createElement('div')
  cuerpo.className = 'card-body'
  const titulo = document.createElement('h3')
  titulo.className = 'h6'
  titulo.textContent = `${dato.categoria.replaceAll('_', ' ')} — ${dato.asunto}`
  const metadatos = document.createElement('p')
  metadatos.className = 'small text-muted'
  metadatos.textContent = `${dato.fecha} · ${dato.autorNombre} · ${dato.estado}`
  const observacion = document.createElement('p')
  observacion.className = 'texto-con-saltos'
  observacion.textContent = dato.observacion
  cuerpo.append(titulo, metadatos, observacion)
  const evidencias = (await listarEvidencias(dato.id)).datos
  if (evidencias.length) {
    const lista = document.createElement('div')
    lista.className = 'd-flex flex-wrap gap-2 mb-3'
    for (const evidencia of evidencias) {
      const enlace = document.createElement('a')
      enlace.className = 'btn btn-sm btn-outline-secondary'
      enlace.href = `/api/v1/evidencias-observacion/${evidencia.id}/archivo`
      enlace.target = '_blank'
      enlace.rel = 'noopener'
      enlace.textContent = evidencia.nombreOriginal
      lista.append(enlace)
    }
    cuerpo.append(lista)
  }
  if (dato.autoevaluacion) {
    const autoevaluacion = document.createElement('p')
    autoevaluacion.className = 'border-start border-primary ps-3'
    autoevaluacion.textContent = `Respuesta estudiante: ${dato.autoevaluacion}`
    cuerpo.append(autoevaluacion)
  }
  if (
    roles.includes('ESTUDIANTE') &&
    dato.estado === 'ABIERTA' &&
    expediente?.estado === 'ABIERTO'
  ) {
    const formulario = document.createElement('form')
    formulario.className = 'row g-2'
    formulario.innerHTML = `<div class="col-md-5"><label class="form-label">Evidencia (opcional)</label><input class="form-control" name="evidencias" type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg"></div><div class="col-md-7"><label class="form-label">Respuesta estudiante</label><textarea class="form-control" name="autoevaluacion" maxlength="2000" rows="3" required></textarea></div><div class="col-12"><button class="btn btn-primary" type="submit">Responder y cerrar observación</button></div>`
    formulario.addEventListener('submit', (evento) => {
      evento.preventDefault()
      const archivos = Array.from(
        formulario.querySelector<HTMLInputElement>('[name=evidencias]')?.files ?? [],
      )
      const autoevaluacion =
        formulario.querySelector<HTMLTextAreaElement>('[name=autoevaluacion]')?.value ?? ''
      if (!autoevaluacion.trim()) return
      mostrarCarga('Verificando y subiendo las evidencias…')
      void archivos
        .reduce(
          (anterior, archivo) => anterior.then(() => agregarEvidencia(dato.id, archivo)),
          Promise.resolve<unknown>(undefined),
        )
        .then(() => completarObservacion(dato, autoevaluacion))
        .then(cargar)
        .then(() => informar('Observación completada y cerrada.'))
        .catch((error: unknown) => informar(textoError(error), 'danger'))
        .finally(cerrarCarga)
    })
    cuerpo.append(formulario)
  }
  articulo.append(cuerpo)
  return articulo
}

async function cargar(): Promise<void> {
  if (!expediente) return
  const datos = (await listarObservaciones(expediente.id)).datos
  elemento('#lista-observaciones').replaceChildren(...(await Promise.all(datos.map(tarjeta))))
  elemento('#vacio-observaciones').classList.toggle('d-none', datos.length > 0)
}

export async function mostrarObservaciones(
  nuevo: Expediente,
  nuevosRoles: string[],
): Promise<void> {
  expediente = nuevo
  roles = nuevosRoles
  elemento('#panel-observaciones').classList.remove('d-none')
  const puedeCrear =
    nuevo.estado === 'ABIERTO' && roles.some((rol) => ['ASESOR', 'TITULAR'].includes(rol))
  elemento('#formulario-observacion').classList.toggle('d-none', !puedeCrear)
  const tipo = elemento<HTMLSelectElement>('#tipo-observacion')
  const tipos: TipoObservacion[] = roles.includes('TITULAR') ? ['TITULAR'] : ['ASESOR']
  tipo.replaceChildren(
    ...tipos.map((valor) => {
      const opcion = document.createElement('option')
      opcion.value = valor
      opcion.textContent = valor.charAt(0) + valor.slice(1).toLowerCase()
      return opcion
    }),
  )
  await cargar()
  elemento('#panel-observaciones').scrollIntoView({ behavior: 'smooth', block: 'start' })
}

elemento<HTMLFormElement>('#formulario-observacion').addEventListener('submit', (evento) => {
  evento.preventDefault()
  const formulario = evento.currentTarget as HTMLFormElement
  formulario.classList.add('was-validated')
  if (!formulario.checkValidity() || !expediente) return
  const tipo = elemento<HTMLSelectElement>('#tipo-observacion').value as TipoObservacion
  const categoria = (elemento<HTMLSelectElement>('#categoria-observacion')?.value ??
    'TRABAJO_EN_CLASE') as CategoriaObservacion
  void crearObservacion(expediente.id, {
    tipo,
    categoria,
    asunto: elemento<HTMLInputElement>('#asunto-observacion').value,
    observacion: elemento<HTMLTextAreaElement>('#texto-observacion').value,
  })
    .then(() => {
      formulario.reset()
      formulario.classList.remove('was-validated')
      return cargar()
    })
    .then(() => informar('Observación creada y notificada.'))
    .catch((error: unknown) => informar(textoError(error), 'danger'))
})
